import {useEffect, useMemo, useState} from 'react'
import {useMutation} from 'react-query'
import {useNavigate} from 'react-router-dom'
import {createPost, getAvatar, getBusinessAvatarByBusinessId} from 'api'
import closeIcon from 'assets/img/close.svg'
import {notifySuccess} from 'utils/toastNotifications'
import useActingAs from 'hooks/useActingAs'
import useBusinessContext from 'hooks/useBusinessContext'
import useRewardAvailability, {
  useInvalidateRewardState,
} from 'hooks/useRewardAvailability'
import RewardCoinHint from 'components/RewardCoinHint'
import postTypes from './postTypes'
import {requireWritable} from 'utils/viewOnly'

/**
 * Composer shell.
 *
 * The composer is a type-agnostic container. It renders the modal chrome
 * (header, author row, primary action button, close affordance) and then
 * delegates body rendering + validation + submission to a post-type
 * plugin from ./postTypes.
 *
 * The actual post-creation side effects are defined by each plugin's
 * `submit()` — for plain posts it uploads attachments; for class/job
 * shares it creates the entity then returns a share payload; for future
 * types (Spaces is the known next) it does whatever that type needs.
 * The shell then hands the returned payload to `POST /posts` uniformly.
 *
 * See ./README.md for the plugin contract and how to add a new type.
 */
const PostComposer = ({
  isOpen,
  onClose,
  onPosted,
  initialTypeId,
  initialBusinessId,
  initialBody,
}) => {
  const navigate = useNavigate()
  const acting = useActingAs()
  // A professional who also has a business can post its jobs and classes
  // without acting as it anywhere else: picking the business here applies
  // to this post only, never to likes, comments or messages.
  const {businesses = []} = useBusinessContext()
  const hasBusinesses = businesses.length > 0
  const [pickedBusinessId, setPickedBusinessId] = useState(null)
  const picked =
    acting.actorKind !== 'business' && pickedBusinessId
      ? businesses.find((b) => b.id === pickedBusinessId) || null
      : null
  const {businessId, business, actor, actorKind} = picked
    ? {
        businessId: picked.id,
        business: picked,
        actor: {
          id: picked.id,
          name: picked.name || '',
          avatarFilename: picked.logoFilename || null,
        },
        actorKind: 'business',
      }
    : acting

  // Which types can this actor use? Recomputed whenever the acting-as
  // identity changes so the tab strip stays accurate without the user
  // having to close and reopen the composer.
  const availableTypes = useMemo(
    () =>
      postTypes.filter((t) =>
        t.canUse({actorKind, businessId, actor, business, hasBusinesses})
      ),
    [actorKind, businessId, actor, business, hasBusinesses]
  )

  const [activeTypeId, setActiveTypeId] = useState(availableTypes[0]?.id)
  // Independent state per type, so switching tabs doesn't wipe the user's
  // in-progress input for the previous type.
  //
  // Plugins receive `{business}` so the listing types (class/job/space)
  // can seed their address from the active business — UAT (2026/05/12)
  // wants new listings to default to the business's address rather than
  // making the owner re-pick it every time.
  const [typeStates, setTypeStates] = useState(() =>
    Object.fromEntries(
      postTypes.map((t) => [t.id, t.createInitialState({business})])
    )
  )
  const [postAsBusiness, setPostAsBusiness] = useState(true)
  const [avatar, setAvatar] = useState(null)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')
  const [running, setRunning] = useState(false)

  const activeType =
    availableTypes.find((t) => t.id === activeTypeId) || availableTypes[0]

  // When the set of available types changes (e.g. user toggles the
  // business switcher outside the composer), snap the active tab back to
  // the first available option if the current one is no longer valid.
  useEffect(() => {
    if (!availableTypes.some((t) => t.id === activeTypeId)) {
      setActiveTypeId(availableTypes[0]?.id)
    }
  }, [availableTypes, activeTypeId])

  // Class / Job plugins require business attribution. When one of those
  // is active, force-pin the acting-as toggle to business mode and hide
  // the user-escape-hatch button.
  const typeRequiresBusiness = Boolean(activeType?.requiresBusiness)

  const authorBusinessId =
    (typeRequiresBusiness || postAsBusiness) && actorKind === 'business'
      ? businessId
      : null

  const authorName =
    authorBusinessId && business?.name ? business.name : actor?.name || 'You'
  const authorCtx = {
    businessId: authorBusinessId,
    userId: actor?.id || null,
    actor,
    business,
  }

  // Picking a business here re-seeds the business-only forms (the job's
  // address defaults to the business's, for example).
  useEffect(() => {
    if (!picked) return
    setTypeStates((prev) => ({
      ...prev,
      ...Object.fromEntries(
        postTypes
          .filter((t) => t.requiresBusiness)
          .map((t) => [t.id, t.createInitialState({business: picked})])
      ),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked?.id])

  // Reset transient state every time the modal opens, so each session
  // starts clean. Per-type form states also reset. If the caller passed
  // an `initialTypeId` (e.g. the trigger card's "Class" / "Job" / "Space"
  // affordances) and that type is available to the current actor, snap
  // to it; otherwise fall back to the first available type.
  useEffect(() => {
    if (!isOpen) return
    setError('')
    setStatus('')
    setPostAsBusiness(true)
    // A caller can open it already posting as one of the person's
    // businesses (Settings > business > Jobs > Post a job).
    setPickedBusinessId(
      initialBusinessId && businesses.some((b) => b.id === initialBusinessId)
        ? initialBusinessId
        : null
    )
    // `initialBody` seeds the plain post's text (the "Post with these
    // tags" button pre-fills a competition's hashtags) and opens on it.
    setTypeStates(
      Object.fromEntries(
        postTypes.map((t) => {
          const state = t.createInitialState({business: acting.business})
          return [
            t.id,
            initialBody && t.id === 'post'
              ? {...state, body: initialBody}
              : state,
          ]
        })
      )
    )
    const requestedTypeId = initialBody ? 'post' : initialTypeId
    const requestedAvailable =
      requestedTypeId && availableTypes.some((t) => t.id === requestedTypeId)
    setActiveTypeId(
      requestedAvailable ? requestedTypeId : availableTypes[0]?.id
    )
    // Keyed on the global identity, not a business picked in this
    // composer (that pick must survive; it re-seeds forms on its own).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    initialTypeId,
    initialBusinessId,
    initialBody,
    acting.business?.id,
  ])

  // Resolve the author's avatar for the composer header.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        if (authorBusinessId) {
          const img = await getBusinessAvatarByBusinessId(
            authorBusinessId,
            80
          ).catch(() => getBusinessAvatarByBusinessId(authorBusinessId))
          if (!cancelled) setAvatar(img || null)
        } else if (actor?.id) {
          const img = await getAvatar(actor.id, 80).catch(() =>
            getAvatar(actor.id)
          )
          if (!cancelled) setAvatar(img || null)
        }
      } catch {
        if (!cancelled) setAvatar(null)
      }
    }
    if (isOpen) load()
    return () => {
      cancelled = true
    }
  }, [isOpen, authorBusinessId, actor?.id])

  const {mutateAsync: submitPost, isLoading: posting} = useMutation((payload) =>
    createPost(payload)
  )

  // Coin hint on the submit button while post_created still pays this
  // member; posting can consume the last award of a capped day, so a
  // successful submit refreshes the availability.
  const rewardAvailability = useRewardAvailability()
  const invalidateRewardState = useInvalidateRewardState()

  // Escape-to-close + body scroll lock while the modal is open. Escape
  // mirrors the backdrop-click guard: disabled mid-submit.
  useEffect(() => {
    if (!isOpen) return undefined
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !running && !posting) onClose?.()
    }
    document.addEventListener('keydown', onKeyDown)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = prevOverflow
    }
  }, [isOpen, running, posting, onClose])

  const activeState = activeType ? typeStates[activeType.id] : null
  const canSubmit =
    activeType && activeState && activeType.canSubmit(activeState) && !running

  const handleSubmit = async () => {
    if (!canSubmit) return
    // Investors: stop before any media uploads.
    if (!requireWritable()) return
    setError('')
    setStatus('')
    setRunning(true)
    try {
      const payload = await activeType.submit({
        state: activeState,
        authorCtx,
        setStatus,
      })
      if (!payload) {
        throw new Error('Could not prepare the post payload.')
      }
      const result = await submitPost(payload)
      if (!result?.id) {
        throw new Error('Post did not return an id.')
      }
      notifySuccess({message: 'Posted'})
      invalidateRewardState()
      if (onPosted) onPosted(result)
      onClose?.()
    } catch (e) {
      setError(
        e?.message || 'Something went wrong while posting. Please try again.'
      )
    } finally {
      setRunning(false)
      setStatus('')
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !running && !posting) onClose?.()
  }

  const handleGoToProfile = () => {
    const target = authorBusinessId
      ? `/business/${authorBusinessId}`
      : '/profile'
    onClose?.()
    navigate(target)
  }

  const setActiveTypeState = (updater) => {
    setTypeStates((prev) => ({
      ...prev,
      [activeType.id]:
        typeof updater === 'function' ? updater(prev[activeType.id]) : updater,
    }))
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Create a post"
    >
      {/* Listing types (class/job/space) get a wider panel — their
          multi-section forms breathe better at 2xl. The free post type
          keeps the tighter xl width. */}
      <div
        className={`bg-white rounded-2xl shadow-large w-full max-h-[90vh] flex flex-col overflow-hidden animate-slideUp transition-[max-width] duration-200 ${
          activeType && activeType.id !== 'post'
            ? 'max-w-xl sm:max-w-2xl'
            : 'max-w-xl'
        }`}
      >
        {/* Single header row. When the actor has more than one available
            post type, the tab strip stands in for the dialog title and
            the active tab tells the user what they're doing. When only
            one type is available (e.g. consumer accounts that can only
            post text/media) we fall back to a plain "Create a post"
            title since there's nothing to switch between. The close
            button always lives on the far right.

            Active-tab indicator is a coral-tinted pill background instead
            of a 2px underline — the underline was getting clipped at
            narrow widths and disappearing under the container's
            border-b in some viewports. */}
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-gray-100">
          {availableTypes.length > 1 ? (
            <div
              role="tablist"
              aria-label="Post type"
              className="flex items-center gap-1 min-w-0 flex-1"
            >
              {availableTypes.map((t) => {
                const isActive = t.id === activeType?.id
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveTypeId(t.id)}
                    disabled={running || posting}
                    className={[
                      'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full transition-all whitespace-nowrap',
                      isActive
                        ? 'bg-sharpenn-coral text-white shadow-sm'
                        : 'text-sharpenn-slate hover:text-sharpenn-black hover:bg-gray-50',
                    ].join(' ')}
                  >
                    <span className="inline-flex">{t.icon}</span>
                    {t.label}
                  </button>
                )
              })}
            </div>
          ) : (
            <h2 className="font-display font-semibold text-sharpenn-black text-base px-2">
              Create a post
            </h2>
          )}
          <button
            type="button"
            onClick={() => !running && !posting && onClose?.()}
            className="p-1.5 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <img src={closeIcon} alt="Close" className="w-5 h-5" />
          </button>
        </div>

        {/* Author row */}
        <div className="px-5 py-4 flex items-center gap-3 border-b border-gray-100">
          <button
            type="button"
            onClick={handleGoToProfile}
            /* Round when posting as yourself, square when posting as a shop —
               authorBusinessId is the same switch the avatar fetch uses. */
            className={`w-10 h-10 ${
              authorBusinessId ? 'rounded-lg' : 'rounded-full'
            } overflow-hidden flex-shrink-0 bg-ash-beige ring-2 ring-sharpenn-blush/50 hover:ring-sharpenn-coral/40 transition-all`}
            aria-label="View profile"
          >
            {avatar ? (
              <img
                src={avatar}
                alt={authorName}
                className="w-10 h-10 object-cover"
              />
            ) : (
              <div className="w-10 h-10 bg-ash-beige" />
            )}
          </button>
          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-sm text-sharpenn-black truncate">
              {authorName}
            </span>
            {/* Acting-as toggle only shown when both (a) actor has a
                business selected, AND (b) the active type allows posting
                as user. Class / Job force business attribution so we
                suppress the toggle there. */}
            {actorKind === 'business' && !typeRequiresBusiness && (
              <button
                type="button"
                onClick={() => setPostAsBusiness((v) => !v)}
                className="text-xs text-sharpenn-coral hover:text-sharpenn-rose font-medium self-start transition-colors"
              >
                {postAsBusiness
                  ? `Posting as ${business?.name || 'your business'} · switch to you`
                  : `Posting as yourself · switch back to ${business?.name || 'your business'}`}
              </button>
            )}
            {picked && typeRequiresBusiness && (
              <button
                type="button"
                onClick={() => setPickedBusinessId(null)}
                className="text-xs text-sharpenn-coral hover:text-sharpenn-rose font-medium self-start transition-colors"
              >
                Posting as {picked.name || 'your business'} · switch to you
              </button>
            )}
            {typeRequiresBusiness && actorKind !== 'business' && (
              <span className="flex flex-wrap items-center gap-x-2 text-xs text-green-gray">
                Post this as
                {businesses.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setPickedBusinessId(b.id)}
                    className="text-sharpenn-coral hover:text-sharpenn-rose font-semibold"
                  >
                    {b.name || 'your business'}
                  </button>
                ))}
              </span>
            )}
          </div>
        </div>

        {/* Type-specific body. `scrollbar-gutter: stable` reserves the
            scrollbar track even when the tab content fits without
            scrolling, so the body width stays identical across tabs and
            the form fields don't shift when switching from Post (short)
            to Class (tall). */}
        <div className="px-5 py-4 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
          {activeType && activeState && (
            <activeType.Body
              state={activeState}
              setState={setActiveTypeState}
              setError={setError}
              authorCtx={authorCtx}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-green-gray min-h-[20px]">
            {status && <span>{status}</span>}
            {error && <span className="text-dusty-red">{error}</span>}
            {/* Quiet reminder of the listing's attribution when nothing
                more urgent occupies the slot. */}
            {!status &&
              !error &&
              typeRequiresBusiness &&
              authorBusinessId &&
              business?.name && (
                <span className="text-sharpenn-slate">
                  Posting as {business.name}
                </span>
              )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => !running && !posting && onClose?.()}
              disabled={running || posting}
              className="px-4 py-2 rounded-full text-sm font-medium text-sharpenn-slate hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <RewardCoinHint entry={rewardAvailability.post_created} />
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="px-5 py-2 rounded-full text-sm font-semibold bg-sharpenn-coral text-white shadow-sm hover:bg-sharpenn-rose hover:shadow transition-all active:scale-[0.98] disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none disabled:active:scale-100"
            >
              {running
                ? status || 'Posting...'
                : activeType?.submitLabel || 'Post'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PostComposer
